import { Box } from '@mui/material';
import { Grid } from '@mui/system';
import spreadsheetStyles from 'assets/scss/_variables.scss';
import { CustomContainer } from 'components';
import GridSkeleton from 'components/Grid/GridSkeleton';
import { WaterfallLoadingSkeletonProps } from './WaterfallLoadingSkeleton.props';
import { WATERFALL_CELL_SKELETON_LAYOUT_STYLES } from './WaterfallLoadingSkeleton.styles';

const COMPANY_INPUT_ROW_COUNT = 10;
const FIRM_INPUT_ROW_COUNT = 12;
const SUMMARY_ROW_COUNT = 4;
const WATERFALL_RESULTS_ROW_COUNT = 7;
const FUND_RESULTS_ROW_COUNT = 8;
const SPREADSHEET_CELL_HORIZONTAL_PADDING = 8;
const SPREADSHEET_TITLE_CELL_HORIZONTAL_PADDING = 14;
const SPREADSHEET_LEFT_GUTTER = 12;
const SPREADSHEET_VERTICAL_FRAME_SPACE = 18;
const INPUT_SPREADSHEET_VERTICAL_FRAME_SPACE = 16;
const RESULTS_SPREADSHEET_TOP_OFFSET = 5;
const ADJACENT_SHEET_OVERLAP = 1;
const getSpreadsheetDimension = (value: string, fallback: number) => {
  const dimension = Number.parseFloat(value);

  return Number.isFinite(dimension) ? dimension : fallback;
};
const SPREADSHEET_CELL_WIDTH = getSpreadsheetDimension(spreadsheetStyles.defaultCellWidth, 175);
const SPREADSHEET_TITLE_CELL_WIDTH = getSpreadsheetDimension(spreadsheetStyles.defaultLabelCellWidth, 275);
const SPREADSHEET_CELL_HEIGHT = getSpreadsheetDimension(spreadsheetStyles.cellHeight, 31);
const SPREADSHEET_HEADER_CELL_HEIGHT = getSpreadsheetDimension(spreadsheetStyles.headerCellHeight, 74);
const SPREADSHEET_CELL_SLOT_WIDTH = SPREADSHEET_CELL_WIDTH + SPREADSHEET_CELL_HORIZONTAL_PADDING;
const SPREADSHEET_TITLE_CELL_SLOT_WIDTH = SPREADSHEET_TITLE_CELL_WIDTH + SPREADSHEET_TITLE_CELL_HORIZONTAL_PADDING;

const createRows = (count: number) => Array.from({ length: count }, (_, index) => index);

type WaterfallSkeletonColumn = {
  id: string;
  width: number;
};

const WaterfallGridSkeleton = ({
  columns,
  firstRowHeight,
  hasSpreadsheetGutter,
  rows,
  rowTestId,
  verticalFrameSpace,
}: {
  columns: WaterfallSkeletonColumn[];
  firstRowHeight: number;
  hasSpreadsheetGutter: boolean;
  rows: number;
  rowTestId?: string;
  verticalFrameSpace: number;
}) => (
  <Box
    pb={`${verticalFrameSpace - SPREADSHEET_LEFT_GUTTER}px`}
    pl={hasSpreadsheetGutter ? `${SPREADSHEET_LEFT_GUTTER}px` : 0}
    pt={`${SPREADSHEET_LEFT_GUTTER}px`}>
    {createRows(rows).map(row => {
      const rowHeight = row === 0 ? firstRowHeight : SPREADSHEET_CELL_HEIGHT;

      return (
        <Box data-testid={rowTestId} display="flex" height={rowHeight} key={row}>
          {columns.map(column => (
            <Box
              key={column.id}
              sx={{
                alignItems: 'center',
                backgroundColor: row === 0 || row % 2 === 0 ? 'rgb(241, 245, 249)' : 'background.paper',
                borderBottom: row === 0 || row === rows - 1 ? '1px solid' : undefined,
                borderColor: 'divider',
                boxSizing: 'border-box',
                display: 'flex',
                flex: '0 0 auto',
                px: '2px',
                width: column.width,
                '.waterfall-cell-skeleton': {
                  ...WATERFALL_CELL_SKELETON_LAYOUT_STYLES,
                  height: rowHeight - 4,
                  width: '100%',
                },
              }}>
              <GridSkeleton className="waterfall-cell-skeleton" maxColumns={1} maxRows={1} />
            </Box>
          ))}
        </Box>
      );
    })}
  </Box>
);

const WaterfallSheetSkeleton = ({
  columns,
  firstRowHeight = SPREADSHEET_CELL_HEIGHT,
  hasSpreadsheetGutter = false,
  hasTitleColumn = false,
  overlapPreviousSheet = false,
  rows,
  testId,
  verticalFrameSpace = SPREADSHEET_VERTICAL_FRAME_SPACE,
  width = 'max-content',
}: {
  columns: number;
  firstRowHeight?: number;
  hasSpreadsheetGutter?: boolean;
  hasTitleColumn?: boolean;
  overlapPreviousSheet?: boolean;
  rows: number;
  testId: string;
  verticalFrameSpace?: number;
  width?: number | string;
}) => {
  const dataColumnCount = columns - (hasTitleColumn ? 1 : 0);
  const gridWidth =
    (hasTitleColumn ? SPREADSHEET_TITLE_CELL_SLOT_WIDTH : 0) + dataColumnCount * SPREADSHEET_CELL_SLOT_WIDTH;
  const sheetWidth = (hasSpreadsheetGutter ? SPREADSHEET_LEFT_GUTTER : 0) + gridWidth;
  const outerWidth = overlapPreviousSheet ? sheetWidth - ADJACENT_SHEET_OVERLAP : width;
  const skeletonColumns = createRows(columns).map(column => ({
    id: `${hasTitleColumn && column === 0 ? 'title' : 'data'}-${column + 1}`,
    width: hasTitleColumn && column === 0 ? SPREADSHEET_TITLE_CELL_SLOT_WIDTH : SPREADSHEET_CELL_SLOT_WIDTH,
  }));

  return (
    <Box data-column-count={columns} data-testid={testId} maxWidth="100%" width={outerWidth}>
      <Box ml={overlapPreviousSheet ? `-${ADJACENT_SHEET_OVERLAP}px` : 0} width={sheetWidth}>
        <WaterfallGridSkeleton
          columns={skeletonColumns}
          firstRowHeight={firstRowHeight}
          hasSpreadsheetGutter={hasSpreadsheetGutter}
          rows={rows}
          rowTestId={testId === 'waterfall-input-skeleton' ? 'waterfall-input-skeleton-row' : undefined}
          verticalFrameSpace={verticalFrameSpace}
        />
      </Box>
    </Box>
  );
};

const WaterfallResultsSkeleton = ({
  fundInvestmentSheetCount,
  showFirmTotalSheet,
}: {
  fundInvestmentSheetCount: number;
  showFirmTotalSheet: boolean;
}) => (
  <Box
    className="always-visible waterfall-tables-container"
    data-testid="waterfall-results-skeleton"
    sx={{ mt: 1.5, pt: `${RESULTS_SPREADSHEET_TOP_OFFSET}px` }}>
    <CustomContainer
      display="inline-flex"
      sx={{
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
      wrap="nowrap">
      <Grid className="waterfall-table-container" display="inline-table">
        <WaterfallSheetSkeleton
          columns={3}
          firstRowHeight={SPREADSHEET_CELL_HEIGHT * 2}
          hasSpreadsheetGutter
          hasTitleColumn
          rows={WATERFALL_RESULTS_ROW_COUNT}
          testId="waterfall-results-skeleton-table"
        />
      </Grid>

      {createRows(fundInvestmentSheetCount).map(sheet => (
        <Grid className="waterfall-fund-investments-container" display="inline-table" key={sheet}>
          <WaterfallSheetSkeleton
            columns={2}
            overlapPreviousSheet
            rows={FUND_RESULTS_ROW_COUNT}
            testId="waterfall-results-skeleton-table"
          />
        </Grid>
      ))}

      {showFirmTotalSheet ? (
        <Grid className="waterfall-firm-total-container" display="inline-table">
          <WaterfallSheetSkeleton
            columns={2}
            overlapPreviousSheet
            rows={FUND_RESULTS_ROW_COUNT}
            testId="waterfall-results-skeleton-table"
          />
        </Grid>
      ) : null}
    </CustomContainer>
  </Box>
);

const WaterfallLoadingSkeleton = ({
  fundInvestmentSheetCount = 1,
  isAdditionalCurrencySelected = false,
  isFirmPage = false,
  showFirmTotalSheet = true,
  showInput = true,
  summaryColumnCount = 6,
}: WaterfallLoadingSkeletonProps) => {
  const inputRowCount = isFirmPage ? FIRM_INPUT_ROW_COUNT : COMPANY_INPUT_ROW_COUNT;
  const inputColumnCount = isAdditionalCurrencySelected ? 3 : 2;

  return (
    <CustomContainer
      aria-busy="true"
      aria-label="Updating Waterfall"
      minHeight={showInput ? 520 : 300}
      role="status"
      width="100%">
      {showInput ? (
        <CustomContainer>
          <Grid container alignItems="flex-start" columnSpacing={2} rowSpacing={1} size={{ xs: 12 }}>
            <Grid className="waterfall-input-container" size={isFirmPage ? { sm: 'auto', xs: 12 } : { xs: 12 }}>
              <WaterfallSheetSkeleton
                columns={inputColumnCount}
                hasSpreadsheetGutter
                hasTitleColumn
                rows={inputRowCount}
                testId="waterfall-input-skeleton"
                verticalFrameSpace={INPUT_SPREADSHEET_VERTICAL_FRAME_SPACE}
              />
            </Grid>
          </Grid>

          <Grid className="waterfall-summary-container" size={{ xs: 12 }}>
            <WaterfallSheetSkeleton
              columns={summaryColumnCount}
              firstRowHeight={SPREADSHEET_HEADER_CELL_HEIGHT}
              hasSpreadsheetGutter
              hasTitleColumn
              rows={SUMMARY_ROW_COUNT}
              testId="waterfall-summary-skeleton"
              width="100%"
            />
          </Grid>
        </CustomContainer>
      ) : (
        <Grid className="waterfall-summary-container" size={{ xs: 12 }}>
          <WaterfallSheetSkeleton
            columns={summaryColumnCount}
            firstRowHeight={SPREADSHEET_HEADER_CELL_HEIGHT}
            hasSpreadsheetGutter
            hasTitleColumn
            rows={SUMMARY_ROW_COUNT}
            testId="waterfall-summary-skeleton"
            width="100%"
          />
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <WaterfallResultsSkeleton
          fundInvestmentSheetCount={fundInvestmentSheetCount}
          showFirmTotalSheet={showFirmTotalSheet}
        />
      </Grid>
    </CustomContainer>
  );
};

export default WaterfallLoadingSkeleton;
